// src/services/api.js - Updated with all missing endpoints including Penalty API

import accessManager from '../Auth/Access'; // Import the AccessManager singleton
import BASE_URL from '../config';

// Group API functions - authentication only, no client-side authorization
export const groupsApi = {
  // Get all groups - requires authentication
  getGroups: async (searchQuery = '', page = 1, pageSize = 50) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    // Build query parameters properly for GET request
    const params = new URLSearchParams();
    
    if (searchQuery && searchQuery.trim()) {
      params.append('search', searchQuery.trim());
    }
    
    // Add pagination parameters
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());
    
    // Construct the endpoint with query parameters
    const endpoint = `/groups/${params.toString() ? '?' + params.toString() : ''}`;
    
    console.log('API Request URL:', endpoint); // Debug log
    
    const response = await accessManager.request(endpoint, {
      method: 'GET'
      // Remove body from GET request - this was causing the issue
    });
  
    console.log('API Response:', response); // Debug log
    
    // Handle both paginated and non-paginated responses
    if (response && typeof response === 'object') {
      // If response has pagination structure (results, count, next, previous)
      if (response.results && Array.isArray(response.results)) {
        return {
          results: response.results,
          count: response.count || response.results.length,
          next: response.next,
          previous: response.previous,
          totalPages: response.total_pages || Math.ceil((response.count || 0) / pageSize)
        };
      }
      
      // If response is a direct array
      if (Array.isArray(response)) {
        return {
          results: response,
          count: response.length,
          next: null,
          previous: null,
          totalPages: 1
        };
      }
      
      // If response is an object but not in expected format
      return {
        results: accessManager.ensureArray(response),
        count: 1,
        next: null,
        previous: null,
        totalPages: 1
      };
    }
    
    // Fallback for unexpected response format
    return {
      results: [],
      count: 0,
      next: null,
      previous: null,
      totalPages: 0
    };
  },

  // Get single group details - requires authentication
  getGroupDetails: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/`);
    return response;
  },

  // Create new group - requires authentication
  createGroup: async (groupData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/groups/', {
      method: 'POST',
      body: JSON.stringify(groupData)
    });
    
    return response;
  },

  // Update group - requires authentication
  updateGroup: async (groupId, groupData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/`, {
      method: 'PUT',
      body: JSON.stringify(groupData)
    });
    
    return response;
  },

  // Delete group - requires authentication
  deleteGroup: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/groups/${groupId}/`, {
      method: 'DELETE'
    });
  },

  // Get group performance metrics - requires authentication
  getGroupPerformance: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/performance/`);
    return response;
  },

  // Get group members - requires authentication
  getGroupMembers: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/`);
    return accessManager.ensureArray(response);
  },

  getCurrentPeriodDates: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const response = await accessManager.request(`/groups/${groupId}/current_period_dates/`);
    return response;
  },

  // MISSING METHODS - Financial Summary APIs
  
  // Get group financial summary with optional period filtering
  getGroupFinancialSummary: async (groupId, periodStart = null, periodEnd = null) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    let endpoint = `/groups/${groupId}/financial-summary/`;
    const params = new URLSearchParams();
    
    if (periodStart) params.append('period_start', periodStart);
    if (periodEnd) params.append('period_end', periodEnd);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const response = await accessManager.request(endpoint);
    return response;
  },

  // Get current period summary for a group
  getGroupCurrentSummary: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/current-summary/`);
    return response;
  },

  // Add member to group using ViewSet action
  addMemberToGroup: async (groupId, memberData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/add_member/`, {
      method: 'POST',
      body: JSON.stringify(memberData)
    });
    
    return response;
  }
};

// Member API functions - authentication only
export const membersApi = {
  // Get all members for a group - requires authentication
  getMembers: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    // Use the correct endpoint that matches your Django URL pattern
    const response = await accessManager.request(`/groups/${groupId}/members/`);
    return accessManager.ensureArray(response);
  },

  // Add member to group - requires authentication
  addMember: async (groupId, memberData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/`, {
      method: 'POST',
      body: JSON.stringify(memberData)
    });
    
    return response;
  },

  // Get single member details - requires authentication
  getMemberDetails: async (groupId, memberId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/${memberId}/`);
    return response;
  },

  // Update member - requires authentication
  updateMember: async (groupId, memberId, memberData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/${memberId}/`, {
      method: 'PUT',
      body: JSON.stringify(memberData)
    });
    
    return response;
  },

  // Partial update member - requires authentication
  partialUpdateMember: async (groupId, memberId, memberData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/${memberId}/`, {
      method: 'PATCH',
      body: JSON.stringify(memberData)
    });
    
    return response;
  },

  // Remove member from group - requires authentication
  removeMember: async (groupId, memberId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/groups/${groupId}/members/${memberId}/`, {
      method: 'DELETE'
    });
  },

  // Get member loans - requires authentication
  getMemberLoans: async (groupId, memberId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/members/${memberId}/loans/`);
    return response;
  },

  // Get member performance history - requires authentication
  getMemberPerformanceHistory: async (memberId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/members/${memberId}/performance/history/`);
    return response;
  }
};

// Performance Analysis API functions
export const performanceApi = {
  // Generate performance analysis for a group's current period
  generatePerformanceAnalysis: async (groupId, isSchoolFees = false) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const requestBody = {};
    if (isSchoolFees) {
      requestBody.is_school_fees = true;
    }

    console.log('ðŸ“¤ Request sent to server:', { groupId, endpoint: '/performance/generate/', isSchoolFees, requestBody });

    const response = await accessManager.request(`/groups/${groupId}/performance/generate/`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    return response;
  },

  // Get performance analysis for a group with optional filtering
  getGroupPerformanceAnalysis: async (groupId, filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    let endpoint = `/groups/${groupId}/performance-analysis/`;
    const params = new URLSearchParams();
    
    // period_id, when present, wins on the backend over period_start/
    // period_end/is_school_fees — it resolves to one exact period rather
    // than a date range. Still fine to pass the others alongside it.
    if (filters.period_id) params.append('period_id', filters.period_id);
    if (filters.period_start) params.append('period_start', filters.period_start);
    if (filters.period_end) params.append('period_end', filters.period_end);
    if (filters.status) params.append('status', filters.status);
    
    if (filters.include_inactive !== undefined) {
      params.append('include_inactive', filters.include_inactive);
    }
    
    if (filters.is_school_fees !== undefined) {
      params.append('is_school_fees', filters.is_school_fees);
    }
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }
  
    console.log('ðŸ“¤ Request sent to server:', { groupId, endpoint, filters });
  
    try {
      const response = await accessManager.request(endpoint);
      return response;
    } catch (error) {
      console.error("âŒ [API] Get Group Performance Analysis Error:", error);
      throw error;
    }
  },
  

  // Close a performance period
  closePerformancePeriod: async (groupId, periodData, isSchoolFees = false) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const requestBody = { ...periodData };
    if (isSchoolFees) {
      requestBody.is_school_fees = true;
    }

    const response = await accessManager.request(`/groups/${groupId}/performance/close/`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    return response;
  },

  // Reopen a closed performance period
  reopenPerformancePeriod: async (groupId, periodData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/performance/reopen/`, {
      method: 'POST',
      body: JSON.stringify(periodData)
    });
    
    return response;
  },

  updatePerformanceAnalysis: async (groupId, updateData, isSchoolFees = false) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const requestBody = { ...updateData };
    if (isSchoolFees) {
      requestBody.is_school_fees = true;
    }

    const response = await accessManager.request(`/groups/${groupId}/performance/update/`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    return response;
  },

  // Get comprehensive performance summary for a group
  getGroupPerformanceSummary: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/performance/summary/`);
    return response;
  },
  validatePerformancePeriod: async (groupId, periodId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(
      `/groups/${groupId}/performance/validate/`,
      {
        method: 'POST',
        body: JSON.stringify({ period_id: periodId })
      }
    );
    
    return response;
  }

};

// Penalty API functions - NEW SECTION
export const penaltyApi = {
  // Preview penalties for a specific period
  previewPenalties: async (groupId, periodStart, periodEnd, isSchoolFees = false) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const params = new URLSearchParams({
      period_start: periodStart,
      period_end: periodEnd
    });
    if (isSchoolFees) {
      params.set('is_school_fees', 'true');
    }
  
    const response = await accessManager.request(
      `/groups/${groupId}/penalties/preview/?${params.toString()}`
    );
    return response;
  },
   

  savePenaltyAction: async (groupId, actionData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(
      `/groups/${groupId}/penalties/save-action/`,
      {
        method: 'POST',
        body: JSON.stringify(actionData)
      }
    );
    return response;
  },

  // NEW: Revert penalty action
  revertPenaltyAction: async (groupId, penaltyId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(
      `/groups/${groupId}/penalties/revert-action/`,
      {
        method: 'POST',
        body: JSON.stringify({ penalty_id: penaltyId })
      }
    );
    return response;
  },

  getSavedPenalties: async (groupId, periodEnd, isSchoolFees = false) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const params = new URLSearchParams({ period_end: periodEnd });
    if (isSchoolFees) {
      params.set('is_school_fees', 'true');
    }
  
    const response = await accessManager.request(
      `/groups/${groupId}/penalties/saved/?${params.toString()}`
    );
    return response;
  },

  // Waive penalties for specific members
  waivePenalties: async (groupId, waiveData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/penalties/waive/`, {
      method: 'POST',
      body: JSON.stringify(waiveData)
    });
    return response;
  },

  // Get penalty history for a group
  getPenaltyHistory: async (groupId, filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    let endpoint = `/groups/${groupId}/penalties/history/`;
    const params = new URLSearchParams();
    
    if (filters.period_start) params.append('period_start', filters.period_start);
    if (filters.period_end) params.append('period_end', filters.period_end);
    if (filters.member_id) params.append('member_id', filters.member_id);
    if (filters.penalty_type) params.append('penalty_type', filters.penalty_type);
    if (filters.status) params.append('status', filters.status);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const response = await accessManager.request(endpoint);
    return response;
  },

  // Get penalties for a specific member
  getMemberPenalties: async (groupId, memberId, filters = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    let endpoint = `/groups/${groupId}/members/${memberId}/penalties/`;
    const params = new URLSearchParams();
    
    if (filters.period_start) params.append('period_start', filters.period_start);
    if (filters.period_end) params.append('period_end', filters.period_end);
    if (filters.penalty_type) params.append('penalty_type', filters.penalty_type);
    if (filters.status) params.append('status', filters.status);
    
    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const response = await accessManager.request(endpoint);
    return response;
  },

  // Update penalty record
  updatePenalty: async (groupId, penaltyId, updateData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/penalties/${penaltyId}/`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });
    return response;
  },

  // Delete penalty record
  deletePenalty: async (groupId, penaltyId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/groups/${groupId}/penalties/${penaltyId}/`, {
      method: 'DELETE'
    });
  },

  // Get penalty configuration/settings for a group
  getPenaltySettings: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/penalties/settings/`);
    return response;
  },

  // Update penalty configuration/settings for a group
  updatePenaltySettings: async (groupId, settingsData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/penalties/settings/`, {
      method: 'PUT',
      body: JSON.stringify(settingsData)
    });
    return response;
  }
};

// Meeting API functions - authentication only
export const meetingsApi = {
  // Get all meetings for a group - requires authentication
  getMeetings: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/meetings/`);
    return response;
  },

  // Create new meeting - requires authentication
  createMeeting: async (groupId, meetingData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/meetings/`, {
      method: 'POST',
      body: JSON.stringify(meetingData)
    });
    
    return response;
  },

  // Get single meeting details - requires authentication
  getMeetingDetails: async (groupId, meetingId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/meetings/${meetingId}/`);
    return response;
  },

  // Update meeting - requires authentication
  updateMeeting: async (groupId, meetingId, meetingData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/meetings/${meetingId}/`, {
      method: 'PUT',
      body: JSON.stringify(meetingData)
    });
    
    return response;
  },

  // Delete meeting - requires authentication
  deleteMeeting: async (groupId, meetingId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    return await accessManager.request(`/groups/${groupId}/meetings/${meetingId}/`, {
      method: 'DELETE'
    });
  },

  // Record attendance for a meeting - requires authentication
  recordAttendance: async (groupId, meetingId, attendanceData) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/groups/${groupId}/meetings/${meetingId}/record_attendance/`, {
      method: 'POST',
      body: JSON.stringify(attendanceData)
    });
    
    return response;
  }
};

// Branch API functions - authentication only
export const branchesApi = {
  // Get all branches - requires authentication
  getBranches: async () => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request('/branches/', {
      method: 'GET'
    });
    
    return accessManager.ensureArray(response);
  },

  // Get single branch details - requires authentication
  getBranchDetails: async (branchId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    const response = await accessManager.request(`/branches/${branchId}/`);
    return response;
  }
};

// Simplified authorization utility functions for UI components
// These can be used for UI display logic, but backend will enforce actual permissions
export const authUtils = {
  // Check if user is authenticated (required for all actions)
  isAuthenticated: () => accessManager.isAuthenticated(),
  
  // Get current user info for UI display
  getCurrentUser: () => accessManager.getCurrentUser(),
  
  // Get user role for UI customization
  getUserRole: () => {
    const user = accessManager.getCurrentUser();
    return user ? user.role : null;
  },
  
  // Get user branch info for UI display
  getUserBranch: () => accessManager.getUserBranch(),
  getUserBranchId: () => accessManager.getUserBranchId(),
  
  // Helper methods for UI role-based display (not for access control)
  isAdmin: () => accessManager.isAdmin(),
  isSecretary: () => accessManager.isSecretary(),
  isUser: () => accessManager.isUser(),
  
  // Generic permission checker - backend will enforce actual permissions
  hasRole: (role) => accessManager.hasRole(role),
  hasAnyRole: (roles) => accessManager.hasAnyRole(roles)
};

// PDF Generation API functions - FIXED for consistent authentication
export const pdfApi = {
  // Generate PDF metadata (returns JSON)
  generateGroupPDF: async (groupId, options = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const requestBody = {};
    if (options.periodStart) requestBody.period_start = options.periodStart;
    if (options.periodEnd) requestBody.period_end = options.periodEnd;
    if (options.download !== undefined) requestBody.download = options.download;
    if (options.isSchoolFees !== undefined) requestBody.is_school_fees = options.isSchoolFees;
  
    console.log('🎯 PDF API Request Body:', requestBody);
  
    const response = await accessManager.request(`/groups/${groupId}/pdf/generate/`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    return response;
  },

  // ✅ Download PDF - ALWAYS use direct fetch, NEVER use accessManager
  downloadPDF: async (groupId, filename) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    console.log('🔽 Starting direct PDF download:', filename);
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}/groups/${groupId}/pdf/download/${filename}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('PDF download failed:', response.status, errorText);
        throw new Error(`PDF download failed: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('✅ PDF blob received:', blob.size, 'bytes');
      
      // Extract filename from headers if available
      const contentDisposition = response.headers.get('content-disposition');
      let finalFilename = filename;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          finalFilename = matches[1].replace(/['"]/g, '');
        }
      }
      
      return { blob, filename: finalFilename };
    } catch (error) {
      console.error('❌ PDF download error:', error);
      throw error;
    }
  },

  // Generate AND download PDF in one call
  generateAndDownloadPDF: async (groupId, options = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    try {
      console.log('📝 Step 1: Generating PDF...');
      
      // Step 1: Generate PDF (returns JSON with filename)
      const pdfInfo = await pdfApi.generateGroupPDF(groupId, {
        ...options,
        download: false
      });

      console.log('✅ Step 1 complete. Filename:', pdfInfo.filename);

      if (!pdfInfo.filename) {
        throw new Error('No filename received from PDF generation');
      }

      console.log('📥 Step 2: Downloading PDF...');

      // Step 2: Download the blob
      const result = await pdfApi.downloadPDF(groupId, pdfInfo.filename);
      
      console.log('✅ Step 2 complete. PDF ready for download');
      
      return result;

    } catch (error) {
      console.error('❌ Generate and download failed:', error);
      throw error;
    }
  },

  // List available PDFs
  listGroupPDFs: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
    return await accessManager.request(`/groups/${groupId}/pdf/list/`);
  },

  // Delete a PDF
  deletePDF: async (groupId, filename) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
    return await accessManager.request(`/groups/${groupId}/pdf/delete/${filename}/`, {
      method: 'DELETE'
    });
  },

  // Trigger browser download
  triggerDownload: (blob, filename = 'document.pdf') => {
    console.log('💾 Triggering download:', filename, 'Size:', blob.size);
    
    try {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ Download triggered successfully');
      }, 100);
    } catch (error) {
      console.error('❌ Download trigger failed:', error);
      throw error;
    }
  }
};

// Excel Generation API functions
export const excelApi = {
  // Generate Excel metadata (returns JSON)
  generateGroupExcel: async (groupId, options = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
  
    const requestBody = {};
    if (options.periodStart) requestBody.period_start = options.periodStart;
    if (options.periodEnd) requestBody.period_end = options.periodEnd;
    if (options.download !== undefined) requestBody.download = options.download;
    if (options.isSchoolFees !== undefined) requestBody.is_school_fees = options.isSchoolFees;
  
    console.log('🎯 Excel API Request Body:', requestBody);
  
    const response = await accessManager.request(`/groups/${groupId}/excel/generate/`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
    
    return response;
  },

  // ✅ Download Excel - ALWAYS use direct fetch, NEVER use accessManager
  downloadExcel: async (groupId, filename) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    console.log('🔽 Starting direct Excel download:', filename);
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}/groups/${groupId}/excel/download/${filename}/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Excel download failed:', response.status, errorText);
        throw new Error(`Excel download failed: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('✅ Excel blob received:', blob.size, 'bytes');
      
      const contentDisposition = response.headers.get('content-disposition');
      let finalFilename = filename;
      
      if (contentDisposition) {
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches && matches[1]) {
          finalFilename = matches[1].replace(/['"]/g, '');
        }
      }
      
      return { blob, filename: finalFilename };
    } catch (error) {
      console.error('❌ Excel download error:', error);
      throw error;
    }
  },

  // Generate AND download Excel in one call
  generateAndDownloadExcel: async (groupId, options = {}) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }

    try {
      console.log('📝 Step 1: Generating Excel...');
      
      const excelInfo = await excelApi.generateGroupExcel(groupId, {
        ...options,
        download: false
      });

      console.log('✅ Step 1 complete. Filename:', excelInfo.filename);

      if (!excelInfo.filename) {
        throw new Error('No filename received from Excel generation');
      }

      console.log('📥 Step 2: Downloading Excel...');

      const result = await excelApi.downloadExcel(groupId, excelInfo.filename);
      
      console.log('✅ Step 2 complete. Excel ready for download');
      
      return result;

    } catch (error) {
      console.error('❌ Generate and download failed:', error);
      throw error;
    }
  },

  // List available Excels
  listGroupExcels: async (groupId) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
    return await accessManager.request(`/groups/${groupId}/excel/list/`);
  },

  // Delete an Excel
  deleteExcel: async (groupId, filename) => {
    if (!accessManager.isAuthenticated()) {
      throw new Error('Authentication required');
    }
    return await accessManager.request(`/groups/${groupId}/excel/delete/${filename}/`, {
      method: 'DELETE'
    });
  },

  // Trigger browser download
  triggerDownload: (blob, filename = 'document.xlsx') => {
    console.log('💾 Triggering download:', filename, 'Size:', blob.size);
    
    try {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        console.log('✅ Download triggered successfully');
      }, 100);
    } catch (error) {
      console.error('❌ Download trigger failed:', error);
      throw error;
    }
  }
};

export { accessManager };

export default {
  groupsApi,
  membersApi,
  performanceApi,
  penaltyApi,
  meetingsApi,
  branchesApi,
  pdfApi,
  excelApi, 
  authUtils
};